import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormHandles } from '@unform/core'
import { debounce } from 'lodash'

import { Button } from '~/components/Buttons'
import Form from '~/components/unform/Form'
import { history } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { ObjectiveBasedConfirmationModal } from '~/modules/retailMedia/components/CampaignForm/elements/ObjectiveBasedConfirmationModal'
import { useAppSelector } from '~/store/hooks'

import PanelSteps from '../../../PanelSteps'
import GoalSelection from '../../elements/ObjectiveSteps/GoalSelection'
import { LinkInsertionOrderStepContent } from '../../elements/ObjectiveSteps/LinkInsertionOrder/LinkInsertionOrderStepContent'
import AutomaticallySelectedProducts from '../../elements/ObjectiveSteps/Products'
import Setup from '../../elements/ObjectiveSteps/Setup'
import { useCreateCampaign } from '../../hooks/useCreateCampaign'
import { useStepsValidation } from '../../hooks/useStepsValidation'
import { DTOCreateObjectiveBasedCampaign } from './dto'
import initialState from './initialState'

const ObjectiveBased: React.FC = () => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const formRef = useRef<FormHandles>(null)

  const params = useQueryParams()

  const initialData: InitialDataObjectiveBased = useMemo(
    () => ({
      ...initialState,
      goal: (params.get('goal') || initialState?.goal) as Goals
    }),
    [params]
  )

  const { selectedProducts } = useAppSelector(
    state => state.retailMedia.campaign.products
  )

  const {
    allStepsValid,
    isManagedByVtex,
    campaignInsertionOrder,
    campaignStrategy,
    campaignProducts,
    campaignSetup,
    handleValidationData
  } = useStepsValidation()

  useEffect(
    () => handleValidationData(initialData),
    [initialData, handleValidationData]
  )

  const {
    handleSubmit: onSubmit,
    loading,
    CreateAlertErrror
  } = useCreateCampaign({
    campaignStrategy: 'objective_based',
    formRef,
    DTOCreateCampaign: DTOCreateObjectiveBasedCampaign
  })

  const executeDebounceFunc = (func: () => void) => func()

  const handleDebounce = useRef(debounce(executeDebounceFunc, 1000)).current

  const handleFormData = useCallback(
    ({ name, value }) => {
      if (formRef?.current?.getData) {
        const newFormData = formRef.current.getData()

        handleValidationData(newFormData)

        if (name === 'goal') {
          params.set('goal', value)
          history.replace({ search: params.toString() })
        }
      }
    },
    [params, handleValidationData]
  )

  const handleChangeForm = useCallback(
    (e: React.ChangeEvent<HTMLFormElement>) => {
      const { name, value } = e.target

      if (name === 'query') return

      if (name === 'name') {
        handleDebounce(() => {
          handleFormData({ name, value })
        })
        return
      }

      handleFormData({ name, value })
    },
    [handleDebounce, handleFormData]
  )

  useEffect(() => {
    handleValidationData({
      products: selectedProducts || []
    })
  }, [selectedProducts, handleValidationData])

  const handleSubmit = useCallback(
    (formData: CreateCampaignObjectiveBasedFormData) => {
      const products = selectedProducts
      const body = { ...formData, products }
      onSubmit(body)
    },
    [onSubmit, selectedProducts]
  )

  const panelSteps = useMemo(() => {
    const steps = [
      isManagedByVtex ? (
        <PanelSteps.Step
          key="campaign_insertion_order"
          title={campaignInsertionOrder.title}
          description={campaignInsertionOrder.description}
          valid={campaignInsertionOrder.isValid}
          disabled={campaignInsertionOrder.isDisabled}
        >
          <LinkInsertionOrderStepContent />
        </PanelSteps.Step>
      ) : null,
      <PanelSteps.Step
        key="campaign_strategy"
        title={campaignStrategy.title}
        description={campaignStrategy.description}
        valid={campaignStrategy.isValid}
        disabled={campaignStrategy.isDisabled}
      >
        <GoalSelection />
      </PanelSteps.Step>,
      <PanelSteps.Step
        key="campaign_products"
        title={campaignProducts.title}
        description={campaignProducts.description}
        valid={campaignProducts.isValid}
        disabled={campaignProducts.isDisabled}
      >
        <AutomaticallySelectedProducts formRef={formRef} />
      </PanelSteps.Step>,
      <PanelSteps.Step
        key="campaign_setup"
        title={campaignSetup.title}
        description={campaignSetup.description}
        valid={campaignSetup.isValid}
        disabled={campaignSetup.isDisabled}
      >
        <Setup initialData={initialState} formRef={formRef} />
      </PanelSteps.Step>
    ]

    return steps.filter(Boolean)
  }, [
    campaignInsertionOrder,
    campaignProducts,
    campaignSetup,
    campaignStrategy,
    isManagedByVtex
  ])

  return (
    <Form
      ref={formRef}
      onChange={handleChangeForm}
      initialData={initialData}
      onSubmit={handleSubmit}
    >
      <PanelSteps>{panelSteps}</PanelSteps>

      <CreateAlertErrror />

      <div className="form-default__buttons">
        <Button
          type="button"
          text={t('rm:createCampaign')}
          loading={loading}
          size="large"
          template="primary"
          disabled={!allStepsValid}
          onClick={() => setIsModalOpen(true)}
        />
      </div>

      <ObjectiveBasedConfirmationModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        formRef={formRef}
        isLoading={loading}
        selectedProducts={selectedProducts}
      />
    </Form>
  )
}

export default ObjectiveBased
